/**
 * PATH: apps/quote/src/components/builder/panels/ai-assistant/AISuggestionsDisplay.tsx
 * PURPOSE: Backend AI suggestions display with accept/reject actions
 */

import { Sparkles, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { SUGGESTION_TYPE_CONFIG, type BackendAISuggestion } from './ai-assistant-types'

function BackendSuggestionCard({
  suggestion, onAccept, onReject,
}: {
  suggestion: BackendAISuggestion; onAccept: () => void; onReject: () => void
}) {
  const config = SUGGESTION_TYPE_CONFIG[suggestion.suggestion_type] || SUGGESTION_TYPE_CONFIG.price_adjustment
  const Icon = config.icon

  const confidenceColors = {
    high: 'text-green-600 bg-green-100 dark:bg-green-900/30',
    medium: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
    low: 'text-slate-500 bg-slate-100 dark:bg-slate-800',
  }

  const formatValue = (value: Record<string, any>): string => {
    if (!value || Object.keys(value).length === 0) return '-'
    return Object.entries(value)
      .map(([k, v]) => {
        if (typeof v === 'number') {
          return k.includes('cost') || k.includes('price') ? formatCurrency(v) : v.toLocaleString()
        }
        return String(v)
      })
      .join(', ')
  }

  return (
    <div className={cn('p-3 rounded-lg border', config.bgColor, 'border-slate-200 dark:border-slate-700')}>
      <div className="flex items-start gap-2 mb-2">
        <div className={cn('p-1.5 rounded', config.bgColor)}>
          <Icon className={cn('w-4 h-4', config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{config.label}</span>
            <span className={cn('px-1.5 py-0.5 text-[10px] font-medium rounded-full', confidenceColors[suggestion.confidence_level])}>
              {Math.round(suggestion.confidence_score * 100)}%
            </span>
          </div>
          {suggestion.line_item_description && (
            <p className="text-xs text-slate-500 truncate mt-0.5">{suggestion.line_item_description}</p>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{suggestion.reasoning}</p>

      {(suggestion.suggestion_type === 'price_adjustment' || suggestion.suggestion_type === 'quantity_check') && (
        <div className="flex items-center gap-2 text-xs mb-2">
          <span className="text-slate-500">{formatValue(suggestion.original_value)}</span>
          <span className="text-slate-400">→</span>
          <span className="font-semibold text-green-600">{formatValue(suggestion.suggested_value)}</span>
        </div>
      )}

      {suggestion.suggestion_type === 'missing_item' && (
        <div className="text-xs text-slate-600 dark:text-slate-400 mb-2 p-2 bg-white dark:bg-slate-800 rounded">
          <span className="font-medium">{suggestion.suggested_value.description}</span>
          <span className="text-slate-400 ml-2">{suggestion.suggested_value.quantity} {suggestion.suggested_value.unit}</span>
        </div>
      )}

      {suggestion.status === 'pending' ? (
        <div className="flex items-center gap-2">
          <button onClick={onAccept} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
            <CheckCircle className="w-3 h-3" /> Accept
          </button>
          <button onClick={onReject} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors">
            <XCircle className="w-3 h-3" /> Dismiss
          </button>
        </div>
      ) : (
        <div className={cn(
          'text-xs font-medium px-2 py-1 rounded text-center',
          suggestion.status === 'accepted'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-slate-100 text-slate-500 dark:bg-slate-800'
        )}>
          {suggestion.status === 'accepted' ? 'Applied' : 'Dismissed'}
        </div>
      )}
    </div>
  )
}

export default function AISuggestionsDisplay({
  suggestions, onAccept, onReject,
}: {
  suggestions: BackendAISuggestion[]; onAccept: (id: string) => void; onReject: (id: string) => void
}) {
  const highConfidenceCount = suggestions.filter(s => s.confidence_level === 'high' && s.status === 'pending').length

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{suggestions.length} suggestions</span>
        </div>
        {highConfidenceCount > 0 && (
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
            {highConfidenceCount} high confidence
          </span>
        )}
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {suggestions.map((suggestion) => (
          <BackendSuggestionCard key={suggestion.id} suggestion={suggestion}
            onAccept={() => onAccept(suggestion.id)} onReject={() => onReject(suggestion.id)} />
        ))}
      </div>
    </div>
  )
}
